/**
 * A widget's port binding read through the machine's roles (B6, 2026-10-19). A port binding names a machine and its
 * ports; the roles route (GET /v1/twin/equipment/{id}/roles, lib/api/equipment-roles) gives each stream bound port its
 * one primary tag, so the board reads that tag by its id and never guesses one by its name: two tags of one name cannot
 * be confused. A port fed by two tags (two_feeds) says so and draws nothing; a port the roles do not list is not bound
 * to the machine. The band a widget draws from the machine's yardstick is the roles route's own (band), never derived
 * here. Pure.
 *
 * Which machine: a board card's port source (DataSource.byEntity) names it by its entity id, and reads only its roles.
 * An older port binding names the machine by its instance name (EntityAliasPicker); when exactly one machine of the
 * plant has that name, its roles resolve the ports they list, and any other key (a parameter, a manual datapoint) is
 * read by its name as before (use-live-tag-data).
 *
 * One series per port (S3, 2026-10-26). A port fed by several sources (its roles list them in `sources`) has one
 * series: for each stretch of time it reads one source, its source of record. The board draws that series (the port's
 * series route, lib/api/port-series), never one source's tag, and takes the tag the roles name (the source it reads
 * now) only for its kind, its name and its band. A port with one source reads its tag by id, as before. A widget keyed
 * by a tag's name that two or more tags share reads the port's series when those tags are the sources of one port
 * (sharedNameKeys, sharedNamePorts, namePortResolutions); a name one tag has is read by its name, as before.
 */
import type { PlantNode } from "@foundry/foundry-plant";
import type { EquipmentRole } from "@/lib/api/equipment-roles";
import type { TagOption, TwinBinding } from "@/lib/api/twin-bindings";
import { normalise } from "@/lib/units/convert";
import { shownFor } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import type { PortTagRef } from "./data-kinds";
import { resolveTwinKpi, type TwinKpiRollups } from "./twin-aggregates";
import type { DataSource } from "./types";
import type { LiveTagData } from "./use-live-tag-data";
import type { TimeseriesSeries } from "@/lib/widgets/types";

/**
 * A port fed by several sources, read as its one series: its machine and port, the series' unit (the roles' unit), and
 * the tags the port may read, whose latest readings say when the port last read anything (a lookback's anchor).
 */
export interface PortSeriesRef {
  entityId: string;
  port: string;
  unit: string;
  sources: string[];
}

/**
 * A bound port as the machine's roles read it: its tag (the one it reads now), with its series when several sources
 * feed it; two feeds that cannot be read as one series; or not bound.
 */
export type PortResolution =
  | { kind: "tag"; tagId: string; tagName: string; role: EquipmentRole; series?: PortSeriesRef }
  | { kind: "two_feeds"; role: EquipmentRole }
  | { kind: "unbound" };

/**
 * The series a port's role reads, or undefined: a port the roles resolve to several sources lists them (S2.2), and reads
 * its series; a port with one source reads its tag. Pure.
 */
export function portSeriesOf(role: EquipmentRole, entityId: string | undefined): PortSeriesRef | undefined {
  if (!entityId || !role.tag_id || (role.sources?.length ?? 0) < 2) return undefined;
  const read = (role.sources ?? []).filter((s) => s.read).map((s) => s.tag_id);
  if (read.length === 0) return undefined;
  return { entityId, port: role.port, unit: role.unit, sources: read };
}

/** A port the roles name a tag for (tagId): its tag, and its series when several sources feed it. Pure. */
function tagResolution(role: EquipmentRole, tagId: string, entityId: string | undefined): PortResolution {
  const series = portSeriesOf(role, entityId);
  return series
    ? { kind: "tag", tagId, tagName: role.tag_name, role, series }
    : { kind: "tag", tagId, tagName: role.tag_name, role };
}

/** The ports a widget reads through its machine's roles, and whether every key is a port of the machine (exclusive). */
export interface PortTags {
  byKey: ReadonlyMap<string, PortResolution>;
  exclusive: boolean;
}

/** The machine a port binding reads through its roles, or null: a tag binding, or a port alias no one machine has. */
export function portEntityOf(
  ds: DataSource | undefined,
  nodes: readonly PlantNode[],
): { entityId: string; exclusive: boolean } | null {
  if (!ds || ds.kind !== "port") return null;
  if (ds.byEntity) return ds.alias ? { entityId: ds.alias, exclusive: true } : null;
  const named = nodes.filter((n) => n.instanceName === ds.alias);
  return named.length === 1 ? { entityId: named[0].id, exclusive: false } : null;
}

/** Every key bound in the source's slots, once each, in slot order. */
export function boundKeysOf(ds: DataSource | undefined): string[] {
  const keys = new Set<string>();
  for (const arr of Object.values(ds?.slots ?? {})) for (const k of arr) keys.add(k);
  return [...keys];
}

/**
 * Each bound key resolved by the machine's roles: its port's tag (with its series when several sources feed it, read
 * from the machine entityId), two feeds, or not bound (a byEntity source's key the roles do not list). Ports match
 * exactly, as the roles route matches them. An older binding's key the roles do not list is left out: it is read by its
 * name. Pure.
 */
export function resolvePorts(
  ds: DataSource | undefined,
  roles: readonly EquipmentRole[],
  exclusive: boolean,
  entityId?: string,
): Map<string, PortResolution> {
  const byPort = new Map(roles.map((r) => [r.port, r] as const));
  const out = new Map<string, PortResolution>();
  for (const key of boundKeysOf(ds)) {
    const role = byPort.get(key);
    if (!role) {
      if (exclusive) out.set(key, { kind: "unbound" });
      continue;
    }
    if (role.state === "two_feeds" || !role.tag_id) out.set(key, { kind: "two_feeds", role });
    else out.set(key, tagResolution(role, role.tag_id, entityId));
  }
  return out;
}

/** Roll-ups that count nothing, for asking only whether a key is one. */
const NO_ROLLUPS: TwinKpiRollups = {
  yieldPct: null, rejectionPct: null, partsProduced: 0, partsAccepted: 0, partsRejected: 0, heats: 0, failedHeats: 0,
};

/**
 * A bare key a KPI reads as a heat or lot roll-up (yield, rejection, parts_produced, heat_count: twin-aggregates.ts
 * resolveTwinKpi), not a tag: a name with no dot that the roll-ups recognise. Never read as a port's series. Pure.
 */
export function isRollupKey(key: string): boolean {
  return !key.includes(".") && resolveTwinKpi(NO_ROLLUPS, key) !== null;
}

/**
 * The keys a widget reads by name whose name two or more of the org's tags share (the case ignored), each with those
 * tags' ids. A key its machine's roles resolve is not read by name, nor is any key of a board card's machine binding
 * (exclusive), nor a bare roll-up key (isRollupKey). Pure.
 */
export function sharedNameKeys(
  keys: readonly string[],
  orgTags: readonly TagOption[],
  ports: PortTags | undefined,
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  if (ports?.exclusive) return out;
  const byName = new Map<string, string[]>();
  for (const t of orgTags) {
    if (!t.name) continue;
    const name = t.name.toLowerCase();
    byName.set(name, [...(byName.get(name) ?? []), t.id]);
  }
  for (const key of keys) {
    if (ports?.byKey.has(key) || isRollupKey(key)) continue;
    const ids = byName.get(key.toLowerCase());
    if (ids && ids.length >= 2) out.set(key, ids);
  }
  return out;
}

/**
 * For each shared name, the one machine port its tags are the sources of: every tag of the name is bound to the same
 * machine's port, as a primary stream binding, and nowhere else. A name whose tags feed two machines, or a tag of which
 * is bound otherwise (a redundant probe, a second port) or not at all, has none: it is read by its name, as before.
 * Pure.
 */
export function sharedNamePorts(
  shared: ReadonlyMap<string, readonly string[]>,
  bindings: readonly TwinBinding[],
): Map<string, { entityId: string; port: string }> {
  const byTag = new Map<string, TwinBinding[]>();
  for (const b of bindings) {
    if (b.purpose && b.purpose !== "stream") continue;
    const id = b.tag_id.toLowerCase();
    byTag.set(id, [...(byTag.get(id) ?? []), b]);
  }
  const out = new Map<string, { entityId: string; port: string }>();
  for (const [key, ids] of shared) {
    let at: { entityId: string; port: string } | undefined;
    let one = true;
    for (const id of ids) {
      const bs = byTag.get(id.toLowerCase()) ?? [];
      if (bs.length !== 1 || bs[0].role !== "primary") {
        one = false;
        break;
      }
      const here = { entityId: bs[0].entity_id, port: bs[0].port };
      if (at && (at.entityId !== here.entityId || at.port !== here.port)) {
        one = false;
        break;
      }
      at = here;
    }
    if (one && at) out.set(key, at);
  }
  return out;
}

/**
 * Each shared name's port, resolved by its machine's roles (rolesOf; undefined while they load): read as the port's
 * series when the roles resolve the port to several sources. A port the roles say is two feeds, or do not list, gives
 * none, and its name is read as before. Pure.
 */
export function namePortResolutions(
  ports: ReadonlyMap<string, { entityId: string; port: string }>,
  rolesOf: (entityId: string) => readonly EquipmentRole[] | undefined,
): Map<string, PortResolution> {
  const out = new Map<string, PortResolution>();
  for (const [key, at] of ports) {
    const role = rolesOf(at.entityId)?.find((r) => r.port === at.port);
    if (!role || role.state === "two_feeds" || !role.tag_id) continue;
    const r = tagResolution(role, role.tag_id, at.entityId);
    if (r.kind === "tag" && r.series) out.set(key, r);
  }
  return out;
}

/**
 * A widget's ports with the names it reads as a port's series beside them (named): the machine's own ports win a key
 * both hold. A board card's machine binding (exclusive) reads nothing by name, so it is left as it is. Pure.
 */
export function withNamePorts(
  ports: PortTags | undefined,
  named: ReadonlyMap<string, PortResolution>,
): PortTags | undefined {
  if (named.size === 0 || ports?.exclusive) return ports;
  const byKey = new Map(named);
  for (const [key, r] of ports?.byKey ?? []) byKey.set(key, r);
  return { byKey, exclusive: false };
}

/**
 * The tag a port key reads, for the binding check: its id and name; null when it reads none (two feeds, not bound). The
 * check finds the tag's stored kind by its id.
 */
export function portTagOf(ports: PortTags | undefined): ((key: string) => PortTagRef | null | undefined) | undefined {
  if (!ports?.exclusive) return undefined;
  return (key) => {
    const r = ports.byKey.get(key);
    if (!r) return undefined;
    return r.kind === "tag" ? { id: r.tagId, name: r.tagName } : null;
  };
}

/** A reason as a sentence: a full stop at its end unless it ends in one. */
function asSentence(s: string): string {
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/**
 * What a widget says of the ports it cannot draw, in one line, or null: each port whose feeds cannot be read as one
 * series (two_feeds), with the roles' reason for it, and each port not bound to the machine. A port fed by several
 * sources that the roles resolve is drawn as its series and says nothing. Pure.
 */
export function portNotes(ports: PortTags | undefined): string | null {
  if (!ports) return null;
  const out: string[] = [];
  const noReason: string[] = [];
  const unbound: string[] = [];
  for (const [key, r] of ports.byKey) {
    if (r.kind === "two_feeds") {
      const why = r.role.two_feeds_reason?.trim();
      if (why) out.push(`${key} is not drawn. ${asSentence(why)}`);
      else noReason.push(key);
    } else if (r.kind === "unbound") unbound.push(key);
  }
  const list = (keys: string[]) =>
    keys.length > 1 ? `${keys.slice(0, -1).join(", ")} and ${keys[keys.length - 1]}` : keys[0];
  if (noReason.length > 0) {
    out.push(
      `${list(noReason)} ${noReason.length === 1 ? "is" : "are"} not drawn because ${
        noReason.length === 1 ? "its" : "their"
      } tags cannot be read as one series.`,
    );
  }
  if (unbound.length > 0) {
    out.push(`Nothing feeds ${list(unbound)} on this machine yet.`);
  }
  return out.length > 0 ? out.join(" ") : null;
}

/** What a widget says while a port it draws as its series is not decided yet (decidingNote). */
export const PORT_DECIDING_NOTE =
  "Still deciding which source to read for the older part of this chart. It settles after the next analysis.";

/**
 * What a widget says of a port read as its series that the platform has not decided yet (S3): its older part is read from
 * the port's first choice alone until the analysis refresh makes the first decision, so the chart is approximate there.
 * `data` is the widget's own live readings (useLiveTagData); null when none of its ports is in that state. Pure.
 */
export function decidingNote(data: Readonly<Record<string, { deciding?: boolean }>> | undefined): string | null {
  return data && Object.values(data).some((d) => d.deciding === true) ? PORT_DECIDING_NOTE : null;
}

/** A band a widget draws on one bound key, in the unit its readings are shown in. */
export interface WidgetBand {
  lo?: number;
  hi?: number;
  label: string;
}

/** A band the card gives: its ends, in its unit (none: the readings' stored unit), with its words. */
interface GivenBand {
  lo?: number;
  hi?: number;
  unit?: string;
  label?: string;
}

function finite(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

/** The widget's band as its config holds it: the machine's yardstick, the card's own ends, or none. */
function configBand(config: Record<string, unknown> | undefined): { yardstick: true } | GivenBand | null {
  const b = config?.band;
  if (!b || typeof b !== "object" || Array.isArray(b)) return null;
  const raw = b as Record<string, unknown>;
  if (raw.yardstick === true) return { yardstick: true };
  const lo = finite(raw.lo);
  const hi = finite(raw.hi);
  if (lo === undefined && hi === undefined) return null;
  return {
    lo,
    hi,
    unit: typeof raw.unit === "string" ? raw.unit.trim() : undefined,
    label: typeof raw.label === "string" && raw.label.trim() ? raw.label.trim() : undefined,
  };
}

/**
 * A band in the unit the key's readings are shown in: as it is when its unit is theirs (or it names none and they
 * convert nothing), converted when its unit is the one the readings are stored in and the org reads them in another;
 * none when the units differ otherwise (a band is never drawn across units). Pure.
 */
function bandShown(band: GivenBand, datum: { unit: string; storedUnit?: string }, prefs: DisplayPrefs | undefined,
  label: string): WidgetBand | null {
  const stored = datum.storedUnit ?? datum.unit;
  const unit = band.unit || stored;
  const ends = { lo: band.lo, hi: band.hi };
  if (normalise(unit) === normalise(datum.unit)) return { ...ends, label };
  if (datum.storedUnit && normalise(unit) === normalise(datum.storedUnit)) {
    const s = shownFor(datum.storedUnit, prefs);
    const conv = (v: number | undefined) => (v === undefined ? undefined : s.convert(v));
    return { lo: conv(ends.lo), hi: conv(ends.hi), label };
  }
  return null;
}

/**
 * The band each bound key draws, by its lower-cased key: the machine's yardstick where the config asks for it (the
 * roles route's band of the key's port: a plant window's ends; a record target is drawn as its setpoint's own line
 * beside, never as a band here), else the card's own ends on every key. A key with no readings draws none. Pure.
 */
export function widgetBands(
  config: Record<string, unknown> | undefined,
  keys: readonly string[],
  ports: PortTags | undefined,
  live: LiveTagData | undefined,
  prefs: DisplayPrefs | undefined,
): Record<string, WidgetBand> {
  const want = configBand(config);
  const out: Record<string, WidgetBand> = {};
  if (!want) return out;
  for (const key of keys) {
    const datum = live?.[key.toLowerCase()];
    if (!datum) continue;
    let band: GivenBand | null = null;
    let label = "the window";
    if ("yardstick" in want) {
      const r = ports?.byKey.get(key);
      const y = r && r.kind === "tag" ? r.role.band : null;
      if (!y || y.kind !== "plant_window" || (y.lo == null && y.hi == null)) continue;
      band = { lo: y.lo ?? undefined, hi: y.hi ?? undefined, unit: y.unit };
      label = y.label || "the plant's window";
    } else {
      band = want;
      label = want.label ?? label;
    }
    const shown = bandShown(band, datum, prefs, label);
    if (shown) out[key.toLowerCase()] = shown;
  }
  return out;
}

/** A number as a band's words show it: at most six significant figures, no trailing zeros. */
function bandNumber(v: number): string {
  return String(Number(v.toPrecision(6)));
}

/**
 * A band in words, for the widget's subtitle: what it is and its ends in the readings' unit, and how it is drawn
 * ("Coloured bars: the plant's window, 1380 to 1420 °C." on a histogram, "Shaded: ..." behind a line; one end alone
 * reads "from" or "up to"). Pure.
 */
export function bandText(band: WidgetBand, unit: string, drawn: "bars" | "shaded" = "bars"): string {
  const u = unit ? ` ${unit}` : "";
  const ends =
    band.lo !== undefined && band.hi !== undefined
      ? `${bandNumber(band.lo)} to ${bandNumber(band.hi)}${u}`
      : band.lo !== undefined
        ? `from ${bandNumber(band.lo)}${u}`
        : `up to ${bandNumber(band.hi as number)}${u}`;
  return `${drawn === "bars" ? "Coloured bars" : "Shaded"}: ${band.label}, ${ends}.`;
}

/** A widget's subtitle with a sentence after it: a full stop between them unless the first ends in one. Pure. */
export function withSentence(base: string | undefined, add: string | null | undefined): string | undefined {
  if (!add) return base || undefined;
  if (!base) return add;
  return /[.!?]$/.test(base.trim()) ? `${base.trim()} ${add}` : `${base.trim()}. ${add}`;
}

/**
 * A band as a line's chart draws it: both ends a shaded range behind the line, one end alone a dashed line (a floor or
 * a ceiling) labelled with what it is. Pure.
 */
export function seriesBand(band: WidgetBand | undefined): Pick<TimeseriesSeries, "referenceRange" | "thresholds"> {
  if (!band) return {};
  if (band.lo !== undefined && band.hi !== undefined) return { referenceRange: { min: band.lo, max: band.hi } };
  const value = band.lo ?? band.hi;
  return value === undefined ? {} : { thresholds: [{ value, label: band.label, kind: "info" }] };
}
